import { Workflow, X } from 'lucide-react';

import { Button, buttonVariants } from '@/components/ui/button';
import { useWorkflowDetail } from '@/hooks/queries/workflow/use-workflow-detail';
import { cn } from '@/lib/utils';
import { WorkflowEditorPage } from '@/pages/workflow/workflow-editor-page';

export interface WorkflowSurfaceProps {
  workflowId: string;
  /** Run to open on the canvas; see `SurfaceDescriptor`. */
  runId?: string;
  /**
   * Told each run the canvas moves to (Past Runs, a run started from the
   * pane, Clear Run), or null when it clears one, so the host can keep the
   * descriptor and its URL naming what the pane shows.
   */
  onRunChange?: (runId: string | null) => void;
  /**
   * Called when the user dismisses the workflow pane. Omit when the chat rail
   * is collapsed: "close" means "go full-page chat", which is meaningless
   * when no chat is visible. The X button is hidden when this is absent.
   */
  onClose?: () => void;
  /**
   * Called after the workflow is deleted. Distinct from `onClose`, which a
   * host may withhold: a deleted workflow leaves nothing to show, so the pane
   * always needs somewhere to go.
   */
  onDeleted?: () => void;
  /**
   * Called when the user activates "View Artifacts" from the console toolbar.
   * When supplied the gallery opens in the host surface slot instead of
   * navigating to the standalone /artifacts page.
   */
  onViewArtifacts?: (workflowId: string) => void;
  /**
   * Re-enables the unsaved-changes navigation blocker on the embedded editor.
   * Only set this when the workflow is the host route's own anchor surface —
   * for a layered peek at another workflow the blocker would trap the user in
   * chat over edits made in a side pane.
   */
  warnOnUnsavedChanges?: boolean;
}

/**
 * Workflow surface for the single-slot workspace: the real editor canvas,
 * minus its node explorer sidebar, so the host's chat rail takes that side.
 *
 * This mounts the editor itself rather than a preview of it — the canvas,
 * console, run banners, and past-runs panel are the same components the
 * `/workflow/:id` route renders, driven by the same global stores. That is
 * what lets agent-proposed canvas mutations apply to what the user sees.
 */
export function WorkflowSurface({
  workflowId,
  runId,
  onRunChange,
  onClose,
  onDeleted,
  onViewArtifacts,
  warnOnUnsavedChanges,
}: WorkflowSurfaceProps) {
  const { data: workflowResponse, isLoading } = useWorkflowDetail(workflowId);

  const detail = workflowResponse?.data;
  // The endpoint's own id is nested under `workflow`, not the top-level
  // `workflow_id` (which is frequently absent — see useWorkflowSummary's
  // fixture); fall back to the payload's copy for older responses.
  const resolvedWorkflowId =
    detail?.workflow?.workflow_id ??
    detail?.workflow_id ??
    detail?.workflow?.payload?.id;
  // useWorkflowDetail keeps the previous workflow's response on screen via
  // placeholderData while a new workflowId's fetch is in flight — ignore it
  // until it actually matches, so a fast pane switch can't paint the old
  // workflow's name in the header.
  const isCurrentWorkflow = !!detail && resolvedWorkflowId === workflowId;
  const workflowName = isCurrentWorkflow
    ? (detail?.name ?? detail?.workflow?.name ?? 'Workflow')
    : undefined;
  const showLoading = isLoading || (!!detail && !isCurrentWorkflow);

  return (
    <div className='relative flex h-full min-w-0 flex-1 flex-col bg-muted/25'>
      {/* `relative` scopes the editor's own loading overlay to this pane. */}
      <div className='relative flex min-h-0 flex-1 flex-col'>
        <WorkflowEditorPage
          embedded
          workflowIdOverride={workflowId}
          runIdOverride={runId}
          onRunChange={onRunChange}
          unsavedChangesWarningEnabled={warnOnUnsavedChanges}
          onViewArtifacts={onViewArtifacts}
          onDeleted={onDeleted}
        />
      </div>

      <div
        className={cn(
          'pointer-events-none absolute left-3 top-2 z-110 flex items-center gap-0.5',
          '*:pointer-events-auto *:rounded-none',
          '[&>*:first-child]:rounded-l-md [&>*:last-child]:rounded-r-md',
        )}
      >
        {/* A label, not a control — styled off the button variants so it sits
            flush with the real buttons beside it. */}
        <div
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'max-w-64 cursor-default',
          )}
        >
          <Workflow className='size-4 shrink-0' />
          <span className='truncate'>{showLoading ? '…' : workflowName}</span>
        </div>

        {onClose && (
          <Button
            variant='outline'
            size='icon'
            aria-label='Close workflow canvas'
            onClick={onClose}
          >
            <X className='size-4' />
          </Button>
        )}
      </div>
    </div>
  );
}
